import { Component, ElementRef, ViewChild, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';

// Enviroment
import { environment } from '../../../environments/environment';
import { User } from '@angular/fire/auth';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
// Removed HttpClient, HttpClientModule, HttpParams
import { Router } from '@angular/router';
import { Subject, Observable, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, map, tap } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { ActoresService } from '../../services/actores.service';
import { SearchService, GlobalHit } from '../../services/search.service';
import { VerifactuService } from '../../services/verifactu.service';
import { ImpersonationService } from '../../services/impersonation.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PerfilDialogComponent } from '../../shared/dialogs/perfil-dialog/perfil-dialog.component';
import { ComunicacionesRecibidasDialogComponent } from '../../shared/dialogs/comunicaciones-recibidas-dialog/comunicaciones-recibidas-dialog.component';
import { AyudaDialogComponent } from '../../shared/dialogs/ayuda-dialog/ayuda-dialog.component';
import { ayudaParaUrl } from '../../shared/help/ayuda-contenido';


@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    MatSidenavModule,
    MatListModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatSnackBarModule,
    MatDialogModule,
    MatTooltipModule,
  ],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss']
})
export class NavbarComponent implements OnInit {
  readonly marca = environment.marca;
  isSuperUser: boolean = false;
  isAdmin: boolean = false;
  user: User | null = null;
  actorNombre: string | null = null;
  env: any = environment;
  ctrl = new FormControl<string | GlobalHit>('');
  results$!: Observable<GlobalHit[]>;
  private resultsForce$ = new Subject<GlobalHit[]>();
  private lastHits: GlobalHit[] = [];
  @ViewChild('globalSearchInput') globalSearchInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger, { static: false }) autoTrigger!: MatAutocompleteTrigger;

  get isSimulating(): boolean { return this.impersonation.active; }

  constructor(
    public auth: AuthService,
    private actoresService: ActoresService,
    private router: Router,
    private search: SearchService,
    private verifactuService: VerifactuService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    public impersonation: ImpersonationService
  ) {
    const query$ = this.ctrl.valueChanges.pipe(
      debounceTime(180),
      map(v => typeof v === 'string' ? v : (v?._title ?? '')),
      tap(q => { const t = q.trim(); if (t.length >= 2) { this.autoTrigger?.openPanel(); } else { this.autoTrigger?.closePanel(); } }),
      distinctUntilChanged(),
      filter(q => q.trim().length >= 2),
      switchMap(q => this.search.search(q.trim())),
      tap(hits => { this.lastHits = hits; })
    );

    this.results$ = merge(this.resultsForce$, query$);
  }

  ngOnInit(): void {
    this.auth.currentUser.subscribe(user => {
      this.user = user;
      this.isSuperUser = environment.superUsers.includes(user?.email ?? '');
      this.isAdmin = environment.administradores.includes(user?.email ?? '');
      if (user?.email) {
        this.actoresService.getActorByEmail(user.email).subscribe({
          next: actor => {
            const nombre = [actor.Nom, actor.Cognom1, actor.Cognom2].filter(Boolean).join(' ');
            this.actorNombre = nombre || null;
          },
          error: () => {}
        });
      } else {
        this.actorNombre = null;
      }
    });
    this.env = environment;
  }

  openPerfil() {
    this.dialog.open(PerfilDialogComponent, { width: '680px', maxWidth: '96vw', maxHeight: '90vh' });
  }

  openComunicacionesRecibidas() {
    this.dialog.open(ComunicacionesRecibidasDialogComponent, { width: '760px', maxWidth: '96vw', maxHeight: '90vh' });
  }

  openAyuda() {
    this.dialog.open(AyudaDialogComponent, {
      data: ayudaParaUrl(this.router.url),
      width: '560px',
      maxWidth: '96vw',
      maxHeight: '90vh',
    });
  }

  logout() {
    this.auth.logout();
  }

  exportarVeriFactu(): void {
    this.verifactuService.exportar({ empresaId: environment.verifactu.empresaId }).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `verifactu_${new Date().toISOString().split('T')[0]}.zip`;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: () => this.snackBar.open('No hay registros VeriFactu para exportar', 'Cerrar', { duration: 4000 })
    });
  }

  onInput(ev: Event) {
    const v = (ev.target as HTMLInputElement).value;
    if (this.ctrl.value !== v) {
      this.ctrl.setValue(v, { emitEvent: true });
    }
  }

  go(hit: GlobalHit) {
    const url = (hit._route || (hit as any).route || '').trim();
    if (!url) return;
    const absolute = url.startsWith('/') ? url : `/${url}`;
    this.router.navigateByUrl(absolute).then(() => {
      this.ctrl.setValue('', { emitEvent: true });
      this.resultsForce$.next([]); // clear panel data
      this.autoTrigger.closePanel();
      this.globalSearchInput?.nativeElement?.blur();
    });
  }

  onKeydown(e: KeyboardEvent) {
    if (e.key === '/') {
      e.preventDefault();
      this.globalSearchInput?.nativeElement?.focus();
    }
  }


  getInitials(name: string | null | undefined, email: string | null | undefined): string {
    if (name) {
      const parts = name.trim().split(' ');
      return parts.map(p => p[0]).join('').slice(0, 2).toUpperCase();
    }
    return email?.[0]?.toUpperCase() || '?';
  }
  
  typeLabel(type: string): string {
    switch (type) {
      case 'entidad':  return 'Entidad';
      case 'encargo':  return 'Encargo';
      case 'documento': return 'Documento';
      case 'actor':    return 'Actor';
      default:         return type;
    }
  }

  displayHit = (hit?: GlobalHit | string | null): string => {
    if (!hit) return '';
    return typeof hit === 'string' ? hit : (hit._title ?? '');
  }
}